/**
 * The plans sheet, opened ON a public tech page (owner, 2026-09-29): a locked
 * section, the short-summary pill or Contact University for a signed-in
 * account that has not paid. It used to send people to Cosmos → Billing,
 * which lost the page they were reading; now they pay here and the page
 * reloads unlocked.
 *
 * The same PricingModal and Razorpay flow (startPlanCheckout) as Cosmos. The
 * trial button still goes to Cosmos → Billing: the trial asks for details in
 * a sheet that lives there.
 */
import React, { useEffect, useState } from 'react';
import PricingModal from '../../../components/PricingModal';
import { apiService, BillingPlan, BillingState } from '../../../services/apiService';
import { trackClick } from '../../../utils/analytics';
import { startPlanCheckout } from '../../../utils/razorpayCheckout';
import { tpTrack } from './track';
import { FRESH_VIEWER_KEY } from './api';

export interface UpgradeAsk {
  /** What was blocked, for the funnel (paywall_shown.feature). */
  feature: string;
  headline?: string;
  message?: string;
}

const BILLING_URL = '/?view=billing';

export const TpUpgradeSheet: React.FC<UpgradeAsk & { techId: string; onClose: () => void }> = ({
  feature, headline, message, techId, onClose,
}) => {
  const [data, setData] = useState<{
    plans: BillingPlan[]; featureOrder: string[]; current: string; trial: any; state: BillingState | null;
    daysLeft: number | null;
  } | null>(null);
  const [busyPlan, setBusyPlan] = useState('');
  const uid = apiService.getStoredUserId?.() || '';

  useEffect(() => {
    let live = true;
    if (!uid) { window.location.href = BILLING_URL; return; }
    (async () => {
      const r = await apiService.getBillingSummary(uid);
      if (!live) return;
      const d: any = r.success && r.data?.success ? r.data : null;
      if (!d || !(d.plans || []).length) { window.location.href = BILLING_URL; return; }
      const sub = d.subscription;
      const onTrial = !!(sub && (sub.is_trial || String(sub.source || '').startsWith('trial')));
      setData({
        plans: d.plans, featureOrder: d.feature_order || [],
        current: onTrial ? 'explorer' : (sub && (sub.status === 'active' || sub.status === 'cancelled') ? sub.plan_code : 'explorer'),
        trial: d.trial || null, state: d.state || null, daysLeft: d.trial_lifecycle?.days_left ?? null,
      });
      // A real paywall hit (they clicked something locked), as in Cosmos.
      trackClick('paywall_shown', { feature, capability: null, plan: sub?.plan_code || 'explorer', surface: 'techpage', tech_id: techId });
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) {
    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40" role="status" aria-live="polite">
        <div className="bg-white rounded-2xl px-6 py-5 text-sm text-grayscale-1200 shadow-lg">Loading plans…</div>
      </div>
    );
  }
  return (
    <div className="relative z-[80]">
      <PricingModal
        plans={data.plans}
        featureOrder={data.featureOrder}
        currentPlanCode={data.current}
        lockedFeature={feature}
        busyPlan={busyPlan}
        trial={data.trial}
        state={data.state}
        trialDaysLeft={data.daysLeft}
        intro={headline ? { headline, message } : undefined}
        onStartTrial={() => { tpTrack('techpage_upgrade_trial', { tech_id: techId, feature }); window.location.href = BILLING_URL; }}
        onChoose={async (plan, coupon, payMethod) => {
          setBusyPlan(plan.code);
          tpTrack('techpage_upgrade_choose', { tech_id: techId, feature, plan: plan.code });
          await startPlanCheckout({
            userId: uid,
            plan,
            coupon,
            payMethod,
            onSuccess: () => {
              tpTrack('techpage_upgrade_paid', { tech_id: techId, feature, plan: plan.code });
              // Same URL again, fetched as the paying account (the flag makes
              // that fetch skip the server's 60 s cache of the viewer's plan).
              try { sessionStorage.setItem(FRESH_VIEWER_KEY, '1'); } catch { /* storage off */ }
              window.location.reload();
            },
            onError: m => alert(m),
            onSettled: () => setBusyPlan(''),
          });
        }}
        onClose={() => { tpTrack('techpage_upgrade_close', { tech_id: techId, feature }); onClose(); }}
      />
    </div>
  );
};

export default TpUpgradeSheet;
